import React, { useState, useEffect } from 'react';
import useBounties from './hooks/useBounties';
import { useToast } from './components/Toast';
import { createBounty, addContribution, claimBounty, requestMeetup, addComment } from './services/api';

// Initial admin data - would come from Google Sheets "Admins" tab
// Columns: email, password, name, meetups (comma-separated), role
const INITIAL_ADMINS = [
  { email: 'murph@example.com', password: 'btcpp2025', name: 'Murph', meetups: ['taipei', 'edmonton'], role: 'super' },
  { email: 'lisa@taipei.btc', password: 'taipei123', name: 'Lisa Chen', meetups: ['taipei'], role: 'organizer' },
  { email: 'bob@edmonton.btc', password: 'yeg2025', name: 'Bob Smith', meetups: ['edmonton'], role: 'organizer' },
  { email: 'yuki@tokyo.btc', password: 'tokyo456', name: 'Yuki Tanaka', meetups: ['tokyo'], role: 'organizer' },
  { email: 'stephen@taipei.btc', password: 'taipei2025', name: 'Stephen', meetups: ['taipei'], role: 'organizer' },
];

// Sample data - would come from Google Sheets
const INITIAL_MEETUPS = [
  { id: 'taipei', name: 'Taipei Bitcoin', country: '🇹🇼', members: 142 },
  { id: 'edmonton', name: 'Edmonton Bitcoin', country: '🇨🇦', members: 67 },
  { id: 'tokyo', name: 'Tokyo Bitcoin', country: '🇯🇵', members: 203 },
  { id: 'berlin', name: 'Berlin Bitcoin', country: '🇩🇪', members: 189 },
];

const businessTypes = [
  '🍜 Restaurant', '💈 Barber Shop', '☕ Coffee Shop', '🏪 Convenience Store',
  '🍺 Bar / Pub', '🏋️ Gym', '🧺 Laundromat', '🛒 Grocery Store',
  '🎮 Gaming Cafe', '📚 Bookstore', '🌸 Spa / Massage', '🔧 Auto Shop',
  '🥟 Night Market Stall', '🧋 Bubble Tea Shop', '🏨 Hotel', '📦 Other'
];

// CommentsSection - MUST be outside BountyBoard to prevent focus loss on re-render
const CommentsSection = ({
  bounty,
  isExpanded,
  onToggle,
  newCommentValue,
  onCommentChange,
  onPostComment,
  commentCount
}) => {
  return (
    <div className="mt-4 pt-4 border-t border-slate-700/50">
      <button
        onClick={onToggle}
        className="flex items-center gap-2 text-slate-400 hover:text-orange-400 transition-colors group"
      >
        <span className="group-hover:scale-110 transition-transform">💬</span>
        <span className="font-medium text-sm">{commentCount} Comment{commentCount !== 1 ? 's' : ''}</span>
        <span className={`text-xs transition-transform duration-300 transform ${isExpanded ? 'rotate-90' : 'rotate-0'}`}>▶</span>
      </button>

      {isExpanded && (
        <div className="mt-3 space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
          {bounty.comments?.map((comment, i) => (
            <div key={i} className="bg-black/20 backdrop-blur-sm border border-slate-700/50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-orange-100 font-medium text-sm">{comment.name}</span>
                <span className="text-slate-500 text-xs">{comment.date}</span>
              </div>
              <p className="text-slate-300 text-sm">{comment.text}</p>
            </div>
          ))}

          {commentCount === 0 && (
            <p className="text-slate-500 text-sm italic">No comments yet. Be the first!</p>
          )}

          {/* Add comment form */}
          <div className="flex gap-2 mt-3 p-1">
            <input
              type="text"
              placeholder="Add a comment..."
              value={newCommentValue}
              onChange={(e) => onCommentChange(e.target.value)}
              className="flex-1 bg-black/30 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-orange-500/50 transition-all"
            />
            <button
              onClick={onPostComment}
              className="bg-orange-600/80 hover:bg-orange-600 px-4 py-2 rounded-lg text-white text-sm font-medium transition-all hover:shadow-[0_0_15px_rgba(234,88,12,0.3)]"
            >
              Post
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const BountyBoard = ({ userEmail = '', userName = '' }) => {
  const [activeTab, setActiveTab] = useState('bounties');
  const [selectedMeetup, setSelectedMeetup] = useState('taipei');
  const [meetups, setMeetups] = useState(INITIAL_MEETUPS);
  const [admins, setAdmins] = useState(INITIAL_ADMINS);

  const { bounties: apiBounties, loading: bountiesLoading, refetch: refetchBounties } = useBounties(selectedMeetup);

  // User Authentication State
  const [user, setUser] = useState(userEmail ? { name: userName, email: userEmail } : null);
  const [showUserLogin, setShowUserLogin] = useState(false);
  const [showUserSignup, setShowUserSignup] = useState(false);
  const [authData, setAuthData] = useState({ email: '', password: '', name: '' });

  const toast = useToast();

  const handleUserLogin = () => {
    // Mock login logic
    if (authData.email && authData.password) {
      setUser({
        name: authData.email.split('@')[0],
        email: authData.email
      });
      setShowUserLogin(false);
      setAuthData({ email: '', password: '', name: '' });
      toast.success('Welcome back!');
    }
  };

  const handleUserSignup = () => {
    // Mock signup logic
    if (authData.email && authData.password && authData.name) {
      setUser({
        name: authData.name,
        email: authData.email
      });
      setShowUserSignup(false);
      setAuthData({ email: '', password: '', name: '' });
      toast.success('Account created! Welcome aboard.');
    }
  };

  const [showNewBounty, setShowNewBounty] = useState(false);
  const [showNewMeetup, setShowNewMeetup] = useState(false);
  const [showContribute, setShowContribute] = useState(null);
  const [showClaimModal, setShowClaimModal] = useState(null);
  const [showWatchModal, setShowWatchModal] = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminError, setAdminError] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [adminTab, setAdminTab] = useState('stats');
  const [expandedComments, setExpandedComments] = useState({});
  const [newComment, setNewComment] = useState({});

  // Password change state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordChangeError, setPasswordChangeError] = useState('');
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState(false);

  // Watch preferences state
  const [watchEmail, setWatchEmail] = useState(userEmail || '');
  const [watchMeetups, setWatchMeetups] = useState(['taipei']);
  const [watchCategories, setWatchCategories] = useState(['💈 Barber Shop', '☕ Coffee Shop']);
  const [watchCategoryPage, setWatchCategoryPage] = useState(0);

  // Form States
  const [newBountyData, setNewBountyData] = useState({
    businessType: businessTypes[0],
    description: '',
    pledge: '',
    name: '',
    email: ''
  });

  const [claimData, setClaimData] = useState({
    businessName: '',
    googleMapsLink: '',
    btcMapLink: '',
    name: '',
    email: '',
    lightningAddress: '',
    notes: ''
  });

  const [contributeData, setContributeData] = useState({
    pledge: '',
    name: '',
    email: ''
  });

  const [newMeetupData, setNewMeetupData] = useState({
    name: '',
    country: '',
    email: ''
  });

  // Handlers
  const handleCreateBounty = async () => {
    if (!newBountyData.description || !newBountyData.pledge) return;

    try {
      const result = await createBounty({
        meetup_id: selectedMeetup,
        business_type: newBountyData.businessType,
        description: newBountyData.description
      });

      if (result.success) {
        // Add initial contribution
        const contribResult = await addContribution({
          bounty_id: result.data.id,
          name: newBountyData.name || 'Anonymous',
          email: newBountyData.email,
          sats: parseInt(newBountyData.pledge)
        });

        if (!contribResult.success) {
          console.error('Failed to add initial contribution', contribResult.error);
          toast.error('Bounty created, but initial pledge failed. Please try adding it manually.');
        } else {
          toast.success('Bounty created successfully! 🚀');
        }

        setShowNewBounty(false);
        setNewBountyData({ ...newBountyData, description: '', pledge: '' }); // Reset fields
        refetchBounties();
      }
    } catch (err) {
      console.error('Failed to create bounty', err);
      toast.error('Failed to create bounty. Please try again.');
    }
  };

  const handleClaimBounty = async () => {
    if (!claimData.businessName || !claimData.googleMapsLink) return;

    try {
      const result = await claimBounty({
        bounty_id: showClaimModal.id,
        claimer_name: claimData.name || 'Anonymous',
        claimer_email: claimData.email,
        business_name: claimData.businessName,
        google_maps_link: claimData.googleMapsLink,
        btc_map_link: claimData.btcMapLink,
        lightning_address: claimData.lightningAddress,
        notes: claimData.notes
      });

      if (result.success) {
        setShowClaimModal(null);
        setClaimData({ businessName: '', googleMapsLink: '', btcMapLink: '', name: '', email: '', lightningAddress: '', notes: '' });
        toast.success('Bounty claimed successfully! 🚀');
        refetchBounties();
      }
    } catch (err) {
      console.error('Failed to claim bounty', err);
      toast.error('Failed to claim bounty. Please try again.');
    }
  };

  const handleContribute = async () => {
    if (!contributeData.pledge) return;

    try {
      const result = await addContribution({
        bounty_id: showContribute,
        name: contributeData.name || 'Anonymous',
        email: contributeData.email,
        sats: parseInt(contributeData.pledge)
      });

      if (result.success) {
        setShowContribute(null);
        setContributeData({ pledge: '', name: '', email: '' });
        toast.success('Contribution added successfully! 🚀');
        refetchBounties();
      }
    } catch (err) {
      console.error('Failed to contribute', err);
      toast.error('Failed to add contribution. Please try again.');
    }
  };

  const handleRequestMeetup = async () => {
    if (!newMeetupData.name || !newMeetupData.email) return;

    try {
      // Generate ID from name (slug)
      const newId = newMeetupData.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

      // Create new meetup object
      const newMeetup = {
        id: newId,
        name: newMeetupData.name,
        country: newMeetupData.country || '🌍',
        members: 1, // Start with 1 member (the organizer)
      };

      // Add to meetups list
      setMeetups(prev => [...prev, newMeetup]);

      // Auto-select the new meetup
      setSelectedMeetup(newId);

      // Also call the API (for backend persistence)
      await requestMeetup(newMeetupData);

      setShowNewMeetup(false);
      setNewMeetupData({ name: '', country: '', email: '' });
      toast.success(`${newMeetupData.name} has been added! Welcome to the community.`);
    } catch (err) {
      console.error('Failed to request meetup', err);
      toast.error('Failed to request meetup. Please try again.');
    }
  };

  // Pre-fill email if user signed up
  useEffect(() => {
    if (userEmail) {
      setWatchEmail(userEmail);
    }
  }, [userEmail]);

  // Paginate business types - 8 per page (2 rows of 4)
  const categoriesPerPage = 8;
  const totalCategoryPages = Math.ceil(businessTypes.length / categoriesPerPage);
  const currentCategories = businessTypes.slice(
    watchCategoryPage * categoriesPerPage,
    (watchCategoryPage + 1) * categoriesPerPage
  );

  // Use API bounties
  const filteredBounties = apiBounties; // Already filtered by meetup from API
  const completedBounties = apiBounties.filter(b => b.status === 'completed');

  const currentMeetup = meetups.find(m => m.id === selectedMeetup);

  // Get meetups this admin can manage
  const getAdminMeetups = () => {
    if (!currentAdmin) return [];
    if (currentAdmin.role === 'super') return meetups;
    return meetups.filter(m => currentAdmin.meetups.includes(m.id));
  };

  const StatusBadge = ({ status }) => {
    const styles = {
      active: 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 shadow-[0_0_15px_rgba(52,211,153,0.15)]',
      pending_verification: 'bg-amber-500/10 text-amber-400 border border-amber-500/20 shadow-[0_0_15px_rgba(251,191,36,0.15)]',
      completed: 'bg-blue-500/10 text-blue-400 border border-blue-500/20 shadow-[0_0_15px_rgba(96,165,250,0.15)]',
    };
    const labels = {
      active: '🎯 Open Bounty',
      pending_verification: '⏳ Pending (33% Released)',
      completed: '✅ Completed',
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-bold backdrop-blur-sm ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const formatSats = (sats) => {
    if (sats >= 1000000) return `${(sats / 1000000).toFixed(2)}M sats`;
    if (sats >= 1000) return `${(sats / 1000).toFixed(0)}K sats`;
    return `${sats} sats`;
  };

  const handleAdminLogin = () => {
    const admin = admins.find(a =>
      a.email.toLowerCase() === adminEmail.toLowerCase() &&
      a.password === adminPassword
    );

    if (admin) {
      setCurrentAdmin(admin);
      setShowAdminLogin(false);
      setShowAdminPanel(true);
      setAdminError('');
      setAdminEmail('');
      setAdminPassword('');
    } else {
      setAdminError('Invalid email or password');
    }
  };

  const handleForgotPassword = () => {
    const admin = admins.find(a => a.email.toLowerCase() === forgotEmail.toLowerCase());

    if (admin) {
      // In production, this would trigger n8n to send an email
      console.log(`Sending password to ${admin.email}: ${admin.password}`);
      setForgotSuccess(true);
    } else {
      setAdminError('No admin account found with this email');
    }
  };

  const handleChangePassword = () => {
    if (newPassword.length < 6) {
      setPasswordChangeError('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordChangeError('Passwords do not match');
      return;
    }

    // Update the password in local state (for mock data persistence during session)
    setAdmins(prevAdmins =>
      prevAdmins.map(admin =>
        admin.email === currentAdmin.email
          ? { ...admin, password: newPassword }
          : admin
      )
    );

    // Also update currentAdmin so the session reflects the change
    setCurrentAdmin(prev => ({ ...prev, password: newPassword }));

    console.log(`Password updated for ${currentAdmin.email}`);
    toast.success('Password updated successfully!');
    setPasswordChangeSuccess(true);
    setPasswordChangeError('');

    // Reset after 2 seconds
    setTimeout(() => {
      setShowChangePassword(false);
      setNewPassword('');
      setConfirmPassword('');
      setPasswordChangeSuccess(false);
    }, 2000);
  };

  const handleAdminLogout = () => {
    setCurrentAdmin(null);
    setShowAdminPanel(false);
    setAdminTab('stats');
  };

  const toggleComments = (bountyId) => {
    setExpandedComments(prev => ({
      ...prev,
      [bountyId]: !prev[bountyId]
    }));
  };

  // CommentsSection is now defined OUTSIDE BountyBoard to prevent focus loss

  return (
    <div className="min-h-screen bg-[#020617] text-slate-200 font-sans selection:bg-orange-500/30 selection:text-orange-200 pb-20">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Outfit:wght@400;700;900&display=swap');
        .font-display { font-family: 'Outfit', sans-serif; }
      `}</style>

      {/* Background Gradients */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-orange-600/10 rounded-full blur-[120px] -translate-y-1/2 mix-blend-screen"></div>
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[120px] translate-y-1/2 mix-blend-screen"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <header className="text-center mb-16 relative">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-slate-700/50 backdrop-blur-md mb-8 shadow-2xl animate-in fade-in slide-in-from-top-4 duration-700 ring-1 ring-white/5">
            <span className="animate-pulse text-orange-400">✨</span>
            <span className="text-xs font-bold tracking-widest uppercase bg-gradient-to-r from-orange-200 to-amber-100 bg-clip-text text-transparent">bitcoin++ taipei hackathon</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-black text-white mb-6 tracking-tight font-display drop-shadow-2xl animate-in zoom-in-95 duration-700 delay-100">
            <span className="bg-gradient-to-r from-white via-slate-200 to-slate-400 bg-clip-text text-transparent">Sats for</span>{' '}
            <span className="bg-gradient-to-r from-orange-400 via-orange-500 to-amber-500 bg-clip-text text-transparent">Shops</span>
          </h1>

          <p className="text-xl md:text-2xl text-slate-400 font-medium mb-10 max-w-2xl mx-auto leading-relaxed animate-in fade-in slide-in-from-bottom-4 duration-700 delay-200">
            Stack sats. Onboard merchants. <span className="text-white">Sovereignty edition.</span>
          </p>

          <div className="flex items-center justify-center gap-8 text-sm font-medium text-slate-500 animate-in fade-in duration-1000 delay-300">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]"></div>
              <span>No Custody</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]"></div>
              <span>P2P</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-purple-500 shadow-[0_0_10px_rgba(168,85,247,0.5)]"></div>
              <span>Censorship Resistant</span>
            </div>
          </div>
        </header>

        {/* Control Bar */}
        <div className="sticky top-4 z-40 bg-slate-900/90 backdrop-blur-xl border border-slate-700/30 p-2 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.4)] mb-12 flex flex-col md:flex-row items-center gap-4 ring-1 ring-white/5">

          {/* Meetup Selector */}
          <div className="relative group">
            <select
              value={selectedMeetup}
              onChange={(e) => setSelectedMeetup(e.target.value)}
              className="appearance-none bg-[#0f172a] border border-slate-700 hover:border-orange-500/50 rounded-xl pl-10 pr-10 py-2.5 text-white font-medium focus:outline-none focus:ring-1 focus:ring-orange-500 transition-all cursor-pointer w-full md:w-auto min-w-[200px]"
            >
              {meetups.map(m => (
                <option key={m.id} value={m.id} className="bg-slate-900">
                  {m.country} {m.name}
                </option>
              ))}
            </select>
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-lg pointer-events-none group-hover:scale-110 transition-transform">🌍</div>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none text-xs">▼</div>
          </div>

          <div className="w-px h-8 bg-slate-700 hidden md:block"></div>

          {/* Stats Summary - Compact */}
          <div className="flex items-center gap-6 overflow-x-auto no-scrollbar w-full md:w-auto px-2">
            <div className="flex items-center gap-3">
              <div className="bg-orange-500/10 p-2 rounded-lg text-orange-400">⚡</div>
              <div>
                <div className="text-sm font-bold text-white leading-none mb-1">
                  {formatSats(filteredBounties.reduce((acc, b) => acc + b.totalSats, 0))}
                </div>
                <div className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">Pooled</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-emerald-500/10 p-2 rounded-lg text-emerald-400">🎯</div>
              <div>
                <div className="text-sm font-bold text-white leading-none mb-1">
                  {filteredBounties.filter(b => b.status === 'active').length}
                </div>
                <div className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">Active</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-blue-500/10 p-2 rounded-lg text-blue-400">👥</div>
              <div>
                <div className="text-sm font-bold text-white leading-none mb-1">
                  {currentMeetup?.members}
                </div>
                <div className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">Members</div>
              </div>
            </div>
          </div>

          <div className="flex-1"></div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 w-full md:w-auto">
            <button
              onClick={() => setShowWatchModal(true)}
              className="flex-1 md:flex-none bg-white/5 hover:bg-white/10 px-4 py-2.5 rounded-xl text-slate-300 text-sm font-medium transition-colors border border-slate-800 hover:border-slate-700/50"
            >
              👁️ Watch
            </button>

            {user ? (
              <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <div className="w-6 h-6 rounded-full bg-gradient-to-r from-orange-500 to-yellow-500 flex items-center justify-center text-[10px] text-black font-bold">
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <span className="text-white text-sm font-medium">{user.name}</span>
                <button
                  onClick={() => setUser(null)}
                  className="ml-2 text-slate-500 hover:text-red-400 text-xs"
                  title="Logout"
                >
                  ✕
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowUserLogin(true)}
                className="flex-1 md:flex-none bg-white/5 hover:bg-slate-800 px-4 py-2.5 rounded-xl text-white text-sm font-bold transition-all border border-slate-700 hover:border-orange-500/30 shadow-lg"
              >
                Login
              </button>
            )}
            <button
              onClick={() => currentAdmin ? setShowAdminPanel(true) : setShowAdminLogin(true)}
              className="flex-1 md:flex-none bg-white/5 hover:bg-white/10 px-4 py-2.5 rounded-xl text-slate-300 text-sm font-medium transition-colors border border-slate-800 hover:border-slate-700/50"
            >
              {currentAdmin ? `Hi, ${currentAdmin.name.split(' ')[0]}` : '🔐 Admin'}
            </button>
            <button
              onClick={() => setShowNewMeetup(true)}
              className="flex-1 md:flex-none bg-orange-600 hover:bg-orange-500 px-4 py-2.5 rounded-xl text-white text-sm font-bold transition-all shadow-lg shadow-orange-500/20 hover:shadow-orange-500/40"
            >
              + New Meetup
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex justify-center mb-10">
          <div className="bg-slate-900/80 backdrop-blur-xl p-1.5 rounded-2xl border border-slate-700/30 inline-flex shadow-[0_8px_32px_rgba(0,0,0,0.3)] ring-1 ring-white/5">
            {[
              { id: 'bounties', label: 'Active Bounties', icon: '🎯' },
              { id: 'completed', label: 'Success Stories', icon: '✅' },
              { id: 'how', label: 'How It Works', icon: '📖' },
            ].map(tab => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 flex items-center gap-2 ${isActive
                    ? 'text-white shadow-lg'
                    : 'text-slate-400 hover:text-white bg-slate-800/40'
                    }`}
                >
                  {isActive && (
                    <div className="absolute inset-0 bg-slate-700 rounded-xl border border-slate-600/50 shadow-sm transition-all z-0" style={{
                      boxShadow: '0 0 20px rgba(0,0,0,0.5)'
                    }}></div>
                  )}
                  <span className="relative z-10 text-lg">{tab.icon}</span>
                  <span className="relative z-10">{tab.label}</span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Main Content */}
        {activeTab === 'bounties' && (
          <div className="space-y-6">
            {/* New Bounty Button */}
            <button
              onClick={() => setShowNewBounty(true)}
              className="group w-full relative overflow-hidden rounded-2xl p-[2px] transition-all duration-300 hover:scale-[1.01] bg-gradient-to-r from-orange-600/60 via-amber-500/60 to-yellow-500/60 hover:from-orange-500 hover:via-amber-400 hover:to-yellow-400"
            >
              <div className="relative bg-[#0f172a] rounded-2xl p-6 flex items-center justify-center gap-4 transition-colors group-hover:bg-slate-900/95">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-orange-500 to-yellow-500 flex items-center justify-center text-xl font-bold text-white shadow-lg">
                  +
                </div>
                <span className="text-xl font-bold bg-gradient-to-r from-orange-200 to-yellow-200 bg-clip-text text-transparent">
                  Create New Bounty
                </span>
              </div>
            </button>

            {/* Loading State */}
            {bountiesLoading && (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent"></div>
                <p className="text-slate-400 mt-4">Loading bounties...</p>
              </div>
            )}

            {/* Empty State */}
            {!bountiesLoading && filteredBounties.length === 0 && (
              <div className="text-center py-12 bg-slate-800/20 rounded-xl border border-slate-700/50">
                <div className="text-6xl mb-4">🎯</div>
                <p className="text-xl text-slate-300 font-medium mb-2">No bounties yet</p>
                <p className="text-slate-400">Be the first to create a bounty for {currentMeetup?.name}!</p>
              </div>
            )}

            {/* Bounty Cards */}
            {!bountiesLoading && filteredBounties.map(bounty => (
              <div
                key={bounty.id}
                className="group relative bg-slate-800/40 backdrop-blur-md rounded-2xl border border-slate-700/30 overflow-hidden transition-all duration-300 hover:border-orange-500/40 hover:shadow-[0_8px_40px_rgba(234,88,12,0.15)] hover:ring-1 hover:ring-orange-500/20"
              >
                <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>

                <div className="p-6 relative">
                  <div className="flex items-start justify-between gap-6 mb-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3 flex-wrap">
                        <div className="bg-slate-700/50 p-2 rounded-lg text-2xl shadow-inner border border-slate-800">
                          {bounty.businessType.split(' ')[0]}
                        </div>
                        <h3 className="text-xl font-bold text-white tracking-tight">
                          {bounty.businessType.slice(2)}
                        </h3>
                        <StatusBadge status={bounty.status} />
                        {bounty.watchers?.length > 0 && (
                          <span className="flex items-center gap-1.5 bg-black/30 border border-slate-700/50 px-2.5 py-1 rounded-full text-slate-400 text-xs font-medium">
                            👁️ {bounty.watchers.length}
                          </span>
                        )}
                      </div>

                      <p className="text-slate-300 text-lg leading-relaxed mb-4 font-light">
                        {bounty.description}
                      </p>

                      {bounty.status === 'pending_verification' && (
                        <div className="mt-4 bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 animate-in fade-in slide-in-from-top-2">
                          <div className="flex items-start gap-4">
                            <div className="bg-amber-500/20 p-2 rounded-lg text-amber-500">📍</div>
                            <div className="flex-1">
                              <div className="text-amber-200 font-bold text-lg mb-1">
                                {bounty.businessName}
                              </div>
                              <div className="text-amber-200/60 text-sm mb-3">
                                Claimed by <span className="text-white font-medium">{bounty.claimedBy}</span> on {bounty.claimedAt}
                              </div>
                              <div className="flex gap-3 flex-wrap">
                                <a
                                  href={bounty.googleMapsLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20 px-4 py-2 rounded-lg text-blue-300 text-sm font-medium transition-colors"
                                >
                                  🗺️ View Map
                                </a>
                                {bounty.btcMapLink && (
                                  <a
                                    href={bounty.btcMapLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/20 px-4 py-2 rounded-lg text-orange-300 text-sm font-medium transition-colors"
                                  >
                                    ₿ BTC Map
                                  </a>
                                )}
                              </div>

                              {/* Verifications */}
                              {bounty.verifications && bounty.verifications.length > 0 && (
                                <div className="mt-4 pt-4 border-t border-amber-500/10">
                                  <div className="text-amber-200/80 text-xs font-bold uppercase tracking-wider mb-3">
                                    Community Verifications ({bounty.verifications.length})
                                  </div>
                                  <div className="space-y-2">
                                    {bounty.verifications.map((v, i) => (
                                      <div key={i} className="flex gap-3 text-sm">
                                        <span className="text-emerald-400">✅</span>
                                        <span className="text-slate-300">
                                          <span className="text-white font-medium">{v.name}</span>: "{v.comment}"
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                  <button className="mt-3 text-emerald-400 hover:text-emerald-300 text-sm font-medium flex items-center gap-1 transition-colors">
                                    <span>+</span> Add my verification
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="text-right flex-shrink-0">
                      <div className="bg-orange-500/10 border border-orange-500/20 rounded-2xl p-4 text-center min-w-[140px]">
                        <div className="text-3xl font-black text-orange-400 drop-shadow-[0_0_15px_rgba(251,146,60,0.3)]">
                          {formatSats(bounty.totalSats)}
                        </div>
                        <div className="text-slate-500 text-xs font-bold uppercase tracking-wider mt-1">
                          Reward Pool
                        </div>
                      </div>
                      <div className="mt-2 text-center">
                        <div className="text-slate-400 text-sm font-medium">
                          {bounty.contributors.length} contributor{bounty.contributors.length !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Contributors */}
                  <div className="flex items-center gap-3 flex-wrap bg-[#020617]/50 rounded-xl p-3 border border-slate-800">
                    <span className="text-slate-500 text-xs font-bold uppercase tracking-wider">Stacked by:</span>
                    {bounty.contributors.map((c, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2 bg-slate-800/50 px-3 py-1.5 rounded-lg border border-slate-800"
                      >
                        <div className={`w-2 h-2 rounded-full ${i === 0 ? 'bg-orange-500' : 'bg-slate-500'}`}></div>
                        <span className="text-sm text-slate-200 font-medium">
                          {c.name}
                        </span>
                        <span className="text-xs text-slate-500 font-mono">
                          {formatSats(c.sats)}
                        </span>
                      </div>
                    ))}
                    {bounty.status === 'active' && (
                      <button
                        onClick={() => setShowContribute(bounty.id)}
                        className="ml-auto bg-orange-600/20 hover:bg-orange-600/30 text-orange-400 border border-orange-500/30 px-4 py-1.5 rounded-lg text-sm font-bold transition-all hover:shadow-[0_0_15px_rgba(234,88,12,0.15)]"
                      >
                        + Add Sats
                      </button>
                    )}
                  </div>

                  {/* Comments Section */}
                  <CommentsSection
                    bounty={bounty}
                    isExpanded={expandedComments[bounty.id]}
                    onToggle={() => toggleComments(bounty.id)}
                    newCommentValue={newComment[bounty.id] || ''}
                    onCommentChange={(val) => setNewComment(prev => ({ ...prev, [bounty.id]: val }))}
                    commentCount={bounty.comments?.length || 0}
                    onPostComment={async () => {
                      if (!newComment[bounty.id]) return;

                      try {
                        await addComment({
                          bounty_id: bounty.id,
                          commenter_name: userName || 'Anonymous',
                          commenter_email: userEmail || '',
                          comment: newComment[bounty.id]
                        });

                        setNewComment(prev => ({ ...prev, [bounty.id]: '' }));
                        refetchBounties();
                      } catch (err) {
                        console.error('Failed to add comment', err);
                      }
                    }}
                  />
                </div>

                {/* Action Bar */}
                {bounty.status === 'active' && (
                  <div className="bg-black/20 border-t border-slate-800 px-6 py-4 flex items-center justify-between flex-wrap gap-4 backdrop-blur-sm">
                    <div className="flex items-center gap-2 text-slate-500 text-sm">
                      <span>🕒</span>
                      <span>Created {bounty.createdAt}</span>
                    </div>
                    <button
                      onClick={() => setShowClaimModal(bounty)}
                      className="bg-emerald-600 hover:bg-emerald-500 px-6 py-2.5 rounded-xl text-white font-bold text-sm transition-all shadow-lg shadow-emerald-600/20 hover:shadow-emerald-600/30 transform hover:-translate-y-0.5"
                    >
                      🎉 I Onboarded a Business!
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'completed' && (
          <div className="space-y-6">
            {completedBounties.filter(b => b.meetup === selectedMeetup).map(bounty => (
              <div
                key={bounty.id}
                className="bg-emerald-900/10 rounded-2xl border border-emerald-500/20 p-6 backdrop-blur-md relative overflow-hidden group hover:border-emerald-500/50 transition-all hover:shadow-[0_8px_40px_rgba(16,185,129,0.15)] hover:ring-1 hover:ring-emerald-500/20"
              >
                <div className="absolute top-0 right-0 p-4 opacity-50">
                  <div className="text-9xl grayscale opacity-5 rotate-12 pointer-events-none">
                    {bounty.businessType.split(' ')[0]}
                  </div>
                </div>

                <div className="flex items-start justify-between flex-wrap gap-6 mb-6 relative z-10">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="bg-emerald-500/20 p-2 rounded-lg text-2xl">
                        {bounty.businessType.split(' ')[0]}
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white">{bounty.businessName}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider shadow-[0_0_10px_rgba(16,185,129,0.2)]">
                            ✅ Verified Merchant
                          </span>
                          <span className="text-slate-400 text-xs">• {bounty.completedAt}</span>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 flex items-center gap-2 text-slate-300">
                      <div className="h-6 w-6 rounded-full bg-orange-500/20 flex items-center justify-center text-xs">👑</div>
                      <span>Onboarded by <span className="text-orange-300 font-bold">{bounty.claimedBy}</span></span>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="bg-emerald-900/40 border border-emerald-500/20 rounded-xl p-4 backdrop-blur-sm">
                      <div className="text-3xl font-bold text-emerald-400 shadow-emerald-500/50 drop-shadow-sm">
                        ⚡ {formatSats(bounty.totalSats)}
                      </div>
                      <div className="text-emerald-200/60 text-xs font-bold uppercase tracking-wider mt-1">
                        Paid out to Contributor
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 mt-3">
                      <a
                        href={bounty.googleMapsLink}
                        className="bg-blue-600/20 hover:bg-blue-600/40 text-blue-300 border border-blue-500/30 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                      >
                        🗺️ Google Maps
                      </a>
                      {bounty.btcMapLink && (
                        <a
                          href={bounty.btcMapLink}
                          className="bg-orange-600/20 hover:bg-orange-600/40 text-orange-300 border border-orange-500/30 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                        >
                          ₿ BTC Map
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {/* Verifications Count */}
                <div className="flex items-center gap-2 mb-4 bg-emerald-950/30 p-2 rounded-lg border border-emerald-500/10 w-fit">
                  <div className="flex -space-x-2">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="w-6 h-6 rounded-full bg-slate-700 border-2 border-slate-800"></div>
                    ))}
                  </div>
                  <span className="text-emerald-300 text-xs font-medium px-1">
                    +{bounty.verifications} community verifications
                  </span>
                </div>

                {/* Comments for completed bounties */}
                <CommentsSection
                  bounty={bounty}
                  isExpanded={expandedComments[bounty.id]}
                  onToggle={() => toggleComments(bounty.id)}
                  newCommentValue={newComment[bounty.id] || ''}
                  onCommentChange={(val) => setNewComment(prev => ({ ...prev, [bounty.id]: val }))}
                  commentCount={bounty.comments?.length || 0}
                  onPostComment={async () => {
                    if (!newComment[bounty.id]) return;

                    try {
                      await addComment({
                        bounty_id: bounty.id,
                        commenter_name: userName || 'Anonymous',
                        commenter_email: userEmail || '',
                        comment: newComment[bounty.id]
                      });

                      setNewComment(prev => ({ ...prev, [bounty.id]: '' }));
                      refetchBounties();
                    } catch (err) {
                      console.error('Failed to add comment', err);
                    }
                  }}
                />
              </div>
            ))}

            {completedBounties.filter(b => b.meetup === selectedMeetup).length === 0 && (
              <div className="text-center py-20 bg-slate-800/30 rounded-3xl border border-slate-800 border-dashed">
                <div className="text-6xl mb-4 opacity-50 grayscale">🏪</div>
                <h3 className="text-white font-bold text-xl mb-2">No merchants yet</h3>
                <p className="text-slate-400 max-w-sm mx-auto">
                  This meetup hasn't onboarded any merchants yet. Be the first to claim a bounty and make history!
                </p>
                <button
                  onClick={() => setActiveTab('bounties')}
                  className="mt-6 text-orange-400 hover:text-orange-300 font-medium"
                >
                  View Active Bounties →
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'how' && (
          <div className="bg-[#0f172a]/40 rounded-3xl border border-slate-700/30 p-8 md:p-12 relative overflow-hidden backdrop-blur-md shadow-[0_8px_32px_rgba(0,0,0,0.3)] ring-1 ring-white/5">
            <div className="absolute top-0 right-0 w-96 h-96 bg-orange-600/10 rounded-full blur-[100px] translate-x-1/3 -translate-y-1/3 pointer-events-none"></div>

            <div className="relative z-10 text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-black text-white mb-6 font-display">How It Works</h2>
              <p className="text-xl text-slate-400 max-w-2xl mx-auto">
                An honor-based system to incentivize real-world Bitcoin adoption.
                <br /><span className="text-orange-400 font-medium">Trust, Verify, Stack.</span>
              </p>
            </div>

            <div className="max-w-4xl mx-auto space-y-12 relative">
              {/* Connecting Line */}
              <div className="absolute left-[27px] md:left-1/2 top-4 bottom-4 w-0.5 bg-gradient-to-b from-orange-500 via-emerald-500 to-blue-500 opacity-30 md:-translate-x-1/2 hidden md:block"></div>

              {[
                { title: 'Create or Stack', desc: 'Post a bounty for a business type or add sats to an existing pledge. No upfront custody.', icon: '🎯', color: 'bg-orange-500' },
                { title: 'Onboard Merchant', desc: 'Visit a business, help them set up Lightning, and submit a claim with proof.', icon: '🤝', color: 'bg-orange-500' },
                { title: 'Verify', desc: 'Community members visit, pay with Bitcoin, and verify the claim.', icon: '✅', color: 'bg-emerald-500' },
                { title: 'Initial Payout', desc: 'Contributors honor the bounty by sending 33% to the onboarder.', icon: '⚡', color: 'bg-emerald-500' },
                { title: 'Long-term Adoption', desc: 'After 3 months of verified activity, the remaining 67% is paid out.', icon: '📅', color: 'bg-blue-500' },
              ].map((step, i) => (
                <div key={i} className={`flex flex-col md:flex-row items-center gap-8 ${i % 2 === 0 ? 'md:flex-row-reverse' : ''}`}>
                  <div className="flex-1 md:text-right">
                    {i % 2 === 0 && (
                      <div className="hidden md:block">
                        <h3 className="text-2xl font-bold text-white mb-2">{step.title}</h3>
                        <p className="text-slate-400">{step.desc}</p>
                      </div>
                    )}
                    {i % 2 !== 0 && (
                      <div className="md:hidden block text-center">
                        <h3 className="text-2xl font-bold text-white mb-2">{step.title}</h3>
                        <p className="text-slate-400">{step.desc}</p>
                      </div>
                    )}
                  </div>

                  <div className={`w-14 h-14 rounded-full ${step.color} flex items-center justify-center text-2xl shadow-[0_0_20px_rgba(255,255,255,0.2)] z-10 shrink-0 border-4 border-[#0f172a]`}>
                    {step.icon}
                  </div>

                  <div className="flex-1">
                    {i % 2 !== 0 && (
                      <div className="hidden md:block">
                        <h3 className="text-2xl font-bold text-white mb-2">{step.title}</h3>
                        <p className="text-slate-400">{step.desc}</p>
                      </div>
                    )}
                    {i % 2 === 0 && (
                      <div className="md:hidden block text-center">
                        <h3 className="text-2xl font-bold text-white mb-2">{step.title}</h3>
                        <p className="text-slate-400">{step.desc}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-6 mt-20">
              <div className="p-8 bg-slate-900/50 rounded-2xl border border-slate-800 backdrop-blur-sm hover:border-orange-500/30 transition-colors">
                <div className="text-3xl mb-4">📧</div>
                <h4 className="font-bold text-white mb-3 text-lg">Smart Notifications</h4>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Stay in the loop. We'll email you when your bounties are claimed, when verification is needed, or when new opportunities pop up in your watched categories.
                </p>
              </div>

              <div className="p-8 bg-slate-900/50 rounded-2xl border border-slate-800 backdrop-blur-sm hover:border-emerald-500/30 transition-colors">
                <div className="text-3xl mb-4">🔗</div>
                <h4 className="font-bold text-white mb-4 text-lg">Community Tools</h4>
                <div className="flex gap-3 flex-wrap">
                  <a href="https://btcmap.org" className="bg-orange-600/20 hover:bg-orange-600/30 text-orange-300 border border-orange-500/30 px-4 py-2 rounded-lg font-medium transition-colors">
                    ₿ BTC Map
                  </a>
                  <a href="https://btcplusplus.dev/conf/taipei" className="bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 border border-blue-500/30 px-4 py-2 rounded-lg font-medium transition-colors">
                    ✨ bitcoin++ Taipei
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* New Bounty Modal */}
        {showNewBounty && (
          <div className="fixed inset-0 bg-[#020617]/80 backdrop-blur-md flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
            <div className="bg-[#0f172a] rounded-3xl border border-slate-700/30 p-8 max-w-md w-full max-h-[90vh] overflow-y-auto shadow-[0_20px_60px_rgba(0,0,0,0.5)] relative ring-1 ring-white/5">
              <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-orange-500 via-yellow-500 to-orange-500"></div>

              <h2 className="text-2xl font-bold text-white mb-6 font-display">Create New Bounty</h2>

              <div className="space-y-5">
                <div>
                  <label className="block text-slate-400 text-sm font-medium mb-2">Business Type</label>
                  <select
                    value={newBountyData.businessType}
                    onChange={(e) => setNewBountyData({ ...newBountyData, businessType: e.target.value })}
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all appearance-none cursor-pointer hover:bg-slate-800/50">
                    {businessTypes.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-slate-400 text-sm font-medium mb-2">Description</label>
                  <textarea
                    value={newBountyData.description}
                    onChange={(e) => setNewBountyData({ ...newBountyData, description: e.target.value })}
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white h-24 focus:outline-none focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all placeholder-slate-600"
                    placeholder="e.g., Any beef noodle shop near Nangang, or a night market stall in Shilin..."
                  />
                </div>

                <div>
                  <label className="block text-slate-400 text-sm font-medium mb-2">Your Pledge (sats)</label>
                  <input
                    type="number"
                    value={newBountyData.pledge}
                    onChange={(e) => setNewBountyData({ ...newBountyData, pledge: e.target.value })}
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all placeholder-slate-600"
                    placeholder="100000"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-400 text-sm font-medium mb-2">Your Name / Nym</label>
                    <input
                      type="text"
                      value={newBountyData.name}
                      onChange={(e) => setNewBountyData({ ...newBountyData, name: e.target.value })}
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all placeholder-slate-600"
                      placeholder="Anonymous"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm font-medium mb-2">Email</label>
                    <input
                      type="email"
                      value={newBountyData.email}
                      onChange={(e) => setNewBountyData({ ...newBountyData, email: e.target.value })}
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all placeholder-slate-600"
                      placeholder="me@example.com"
                    />
                  </div>
                </div>
                <p className="text-slate-500 text-xs">We'll notify you when someone claims this bounty.</p>
              </div>

              <div className="flex gap-3 mt-8">
                <button
                  onClick={() => setShowNewBounty(false)}
                  className="flex-1 px-4 py-3 bg-slate-800 hover:bg-slate-700 rounded-xl text-white font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateBounty}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-500 hover:to-orange-400 rounded-xl text-white font-bold transition-all shadow-lg shadow-orange-500/20"
                >
                  Create Bounty
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Claim Bounty Modal */}
        {showClaimModal && (
          <div className="fixed inset-0 bg-[#020617]/80 backdrop-blur-md flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
            <div className="bg-[#0f172a] rounded-3xl border border-slate-700/30 p-8 max-w-md w-full max-h-[90vh] overflow-y-auto shadow-[0_20px_60px_rgba(0,0,0,0.5)] relative ring-1 ring-white/5">
              <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-500"></div>

              <h2 className="text-2xl font-bold text-white mb-2 font-display">🎉 Claim This Bounty</h2>
              <p className="text-slate-400 mb-6">
                You're claiming: <span className="text-orange-400 font-medium">{showClaimModal.businessType}</span>
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-slate-400 text-sm font-medium mb-2">Business Name</label>
                  <input
                    type="text"
                    value={claimData.businessName}
                    onChange={(e) => setClaimData({ ...claimData, businessName: e.target.value })}
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/50 transition-all placeholder-slate-600"
                    placeholder="e.g., Happy Coffee Shop"
                  />
                </div>

                <div>
                  <label className="block text-slate-400 text-sm font-medium mb-2">Google Maps Link *</label>
                  <input
                    type="url"
                    value={claimData.googleMapsLink}
                    onChange={(e) => setClaimData({ ...claimData, googleMapsLink: e.target.value })}
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/50 transition-all placeholder-slate-600"
                    placeholder="https://maps.google.com/..."
                  />
                  <p className="text-slate-500 text-xs mt-1">
                    Community members will use this to find and verify the business
                  </p>
                </div>

                <div>
                  <label className="block text-slate-400 text-sm font-medium mb-2">BTC Map Link (optional)</label>
                  <input
                    type="url"
                    value={claimData.btcMapLink}
                    onChange={(e) => setClaimData({ ...claimData, btcMapLink: e.target.value })}
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/50 transition-all placeholder-slate-600"
                    placeholder="https://btcmap.org/merchant/..."
                  />
                </div>

                <div>
                  <label className="block text-slate-400 text-sm font-medium mb-2">Your Name / Nym</label>
                  <input
                    type="text"
                    value={claimData.name}
                    onChange={(e) => setClaimData({ ...claimData, name: e.target.value })}
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/50 transition-all placeholder-slate-600"
                    placeholder="BitcoinBob"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-400 text-sm font-medium mb-2">Email</label>
                    <input
                      type="email"
                      value={claimData.email}
                      onChange={(e) => setClaimData({ ...claimData, email: e.target.value })}
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/50 transition-all placeholder-slate-600"
                      placeholder="bob@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm font-medium mb-2">LN Addr (opt)</label>
                    <input
                      type="text"
                      value={claimData.lightningAddress}
                      onChange={(e) => setClaimData({ ...claimData, lightningAddress: e.target.value })}
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/50 transition-all placeholder-slate-600"
                      placeholder="bob@wos.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-slate-400 text-sm font-medium mb-2">Notes</label>
                  <textarea
                    value={claimData.notes}
                    onChange={(e) => setClaimData({ ...claimData, notes: e.target.value })}
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white h-20 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/50 transition-all placeholder-slate-600"
                    placeholder="How did you help them set up? Any tips for verifiers?"
                  />
                </div>
              </div>

              <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-4 mt-6">
                <p className="text-orange-200 text-sm leading-relaxed">
                  <strong>What happens next:</strong> All {showClaimModal.contributors?.length || 0} contributors will be emailed to verify.
                  Visit the business and pay with Bitcoin! After verification, they send 33%. The remaining 67% comes after 3 months.
                </p>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowClaimModal(null)}
                  className="flex-1 px-4 py-3 bg-slate-800 hover:bg-slate-700 rounded-xl text-white font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleClaimBounty}
                  className="flex-1 px-4 py-3 bg-emerald-600 hover:bg-emerald-500 rounded-xl text-white font-bold transition-colors shadow-lg shadow-emerald-600/20"
                >
                  Submit Claim
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Watch Categories Modal */}
        {showWatchModal && (
          <div className="fixed inset-0 bg-[#020617]/80 backdrop-blur-md flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
            <div className="bg-[#0f172a] rounded-3xl border border-slate-700/30 p-8 max-w-lg w-full shadow-[0_20px_60px_rgba(0,0,0,0.5)] relative ring-1 ring-white/5">
              <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-blue-500"></div>

              <h2 className="text-2xl font-bold text-white mb-2 font-display">👁️ Watch Categories</h2>
              <p className="text-slate-400 mb-6">
                Get notified when bounties are claimed in your selected meetups and categories.
              </p>

              {/* Email */}
              <div className="mb-6">
                <label className="block text-slate-400 text-sm font-medium mb-2">Your Email</label>
                <input
                  type="email"
                  value={watchEmail}
                  onChange={(e) => setWatchEmail(e.target.value)}
                  className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 transition-all placeholder-slate-600"
                  placeholder="watcher@example.com"
                />
              </div>

              {/* Meetup Selection */}
              <div className="mb-6">
                <label className="block text-slate-400 text-sm font-medium mb-2">Which meetups?</label>
                <div className="grid grid-cols-2 gap-2">
                  {meetups.map(m => (
                    <label
                      key={m.id}
                      className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all border ${watchMeetups.includes(m.id)
                        ? 'bg-blue-600/20 border-blue-500/50'
                        : 'bg-slate-900/50 border-slate-800 hover:bg-slate-800/50'
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={watchMeetups.includes(m.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setWatchMeetups([...watchMeetups, m.id]);
                          } else {
                            setWatchMeetups(watchMeetups.filter(id => id !== m.id));
                          }
                        }}
                        className="w-4 h-4 rounded border-slate-500 text-blue-500 focus:ring-blue-500 bg-slate-800"
                      />
                      <span className="text-xl">{m.country}</span>
                      <span className="text-white text-sm font-medium">{m.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Category Selection - Tiled with pagination */}
              <div className="mb-6">
                <label className="block text-slate-400 text-sm font-medium mb-2">Which business types?</label>

                {/* Category tiles */}
                <div className="grid grid-cols-4 gap-2 mb-4">
                  {currentCategories.map(type => {
                    const emoji = type.split(' ')[0];
                    const name = type.slice(2);
                    const isSelected = watchCategories.includes(type);
                    return (
                      <button
                        key={type}
                        onClick={() => {
                          if (isSelected) {
                            setWatchCategories(watchCategories.filter(c => c !== type));
                          } else {
                            setWatchCategories([...watchCategories, type]);
                          }
                        }}
                        className={`flex flex-col items-center justify-center p-3 rounded-xl transition-all border h-24 ${isSelected
                          ? 'bg-orange-600/20 border-orange-500/50 shadow-[0_0_15px_rgba(234,88,12,0.1)]'
                          : 'bg-slate-900/50 border-slate-800 hover:bg-slate-800/50'
                          }`}
                      >
                        <span className="text-2xl mb-2">{emoji}</span>
                        <span className={`text-xs font-medium text-center leading-tight ${isSelected ? 'text-orange-200' : 'text-slate-400'}`}>{name}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Pagination arrows */}
                <div className="flex items-center justify-between">
                  <p className="text-slate-500 text-xs">
                    {watchCategories.length} categories selected
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setWatchCategoryPage(p => Math.max(0, p - 1))}
                      disabled={watchCategoryPage === 0}
                      className={`p-2 rounded-lg transition-colors ${watchCategoryPage === 0
                        ? 'text-slate-700 cursor-not-allowed'
                        : 'text-white bg-slate-800 hover:bg-slate-700'
                        }`}
                    >
                      ←
                    </button>
                    <span className="text-slate-400 text-sm py-2 px-2">
                      {watchCategoryPage + 1} / {totalCategoryPages}
                    </span>
                    <button
                      onClick={() => setWatchCategoryPage(p => Math.min(totalCategoryPages - 1, p + 1))}
                      disabled={watchCategoryPage === totalCategoryPages - 1}
                      className={`p-2 rounded-lg transition-colors ${watchCategoryPage === totalCategoryPages - 1
                        ? 'text-slate-700 cursor-not-allowed'
                        : 'text-white bg-slate-800 hover:bg-slate-700'
                        }`}
                    >
                      →
                    </button>
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex gap-3 pt-6 border-t border-slate-800">
                <button
                  onClick={() => {
                    setShowWatchModal(false);
                    setWatchCategoryPage(0);
                  }}
                  className="flex-1 px-4 py-3 bg-slate-800 hover:bg-slate-700 rounded-xl text-white font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    console.log('Watch preferences:', { watchEmail, watchMeetups, watchCategories });
                    setShowWatchModal(false);
                    setWatchCategoryPage(0);
                  }}
                  className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-500 rounded-xl text-white font-bold transition-colors shadow-lg shadow-blue-600/20"
                >
                  Save Preferences
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Admin Login Modal */}
        {showAdminLogin && (
          <div className="fixed inset-0 bg-[#020617]/80 backdrop-blur-md flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
            <div className="bg-[#0f172a] rounded-3xl border border-slate-700/30 p-8 max-w-sm w-full shadow-[0_20px_60px_rgba(0,0,0,0.5)] relative ring-1 ring-white/5">
              <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-slate-500 via-white to-slate-500 opacity-50"></div>

              <h2 className="text-2xl font-bold text-white mb-6 font-display text-center">🔐 Admin Login</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-slate-400 text-sm font-medium mb-2">Email</label>
                  <input
                    type="email"
                    value={adminEmail}
                    onChange={(e) => {
                      setAdminEmail(e.target.value);
                      setAdminError('');
                    }}
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-white/30 focus:ring-1 focus:ring-white/30 transition-all placeholder-slate-600"
                    placeholder="admin@meetup.com"
                  />
                </div>

                <div>
                  <label className="block text-slate-400 text-sm font-medium mb-2">Password</label>
                  <input
                    type="password"
                    value={adminPassword}
                    onChange={(e) => {
                      setAdminPassword(e.target.value);
                      setAdminError('');
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && handleAdminLogin()}
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-white/30 focus:ring-1 focus:ring-white/30 transition-all placeholder-slate-600"
                    placeholder="Enter password"
                  />
                </div>

                {adminError && (
                  <p className="text-red-400 text-sm text-center bg-red-900/20 border border-red-500/20 p-2 rounded-lg">{adminError}</p>
                )}

                <button
                  onClick={() => {
                    setShowAdminLogin(false);
                    setShowForgotPassword(true);
                    setAdminError('');
                  }}
                  className="text-slate-400 hover:text-white text-sm transition-colors w-full text-center"
                >
                  Forgot password?
                </button>
              </div>

              <div className="flex gap-3 mt-8">
                <button
                  onClick={() => {
                    setShowAdminLogin(false);
                    setAdminEmail('');
                    setAdminPassword('');
                    setAdminError('');
                  }}
                  className="flex-1 px-4 py-3 bg-slate-800 hover:bg-slate-700 rounded-xl text-white font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAdminLogin}
                  className="flex-1 px-4 py-3 bg-white text-black hover:bg-slate-200 rounded-xl font-bold transition-colors shadow-[0_0_20px_rgba(255,255,255,0.1)]"
                >
                  Login
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <div className="fixed inset-0 bg-[#020617]/80 backdrop-blur-md flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
            <div className="bg-[#0f172a] rounded-3xl border border-slate-700/30 p-8 max-w-sm w-full shadow-[0_20px_60px_rgba(0,0,0,0.5)] relative ring-1 ring-white/5">
              <h2 className="text-2xl font-bold text-white mb-4 font-display text-center">🔑 Forgot Password</h2>

              {!forgotSuccess ? (
                <>
                  <p className="text-slate-400 mb-6 text-center text-sm">
                    Enter your admin email and we'll send you your password.
                  </p>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-slate-400 text-sm font-medium mb-2">Email</label>
                      <input
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => {
                          setForgotEmail(e.target.value);
                          setAdminError('');
                        }}
                        className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-white/30 focus:ring-1 focus:ring-white/30 transition-all placeholder-slate-600"
                        placeholder="admin@meetup.com"
                      />
                    </div>

                    {adminError && (
                      <p className="text-red-400 text-sm text-center bg-red-900/20 border border-red-500/20 p-2 rounded-lg">{adminError}</p>
                    )}
                  </div>

                  <div className="flex gap-3 mt-8">
                    <button
                      onClick={() => {
                        setShowForgotPassword(false);
                        setForgotEmail('');
                        setAdminError('');
                      }}
                      className="flex-1 px-4 py-3 bg-slate-800 hover:bg-slate-700 rounded-xl text-white font-medium transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleForgotPassword}
                      className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-500 rounded-xl text-white font-bold transition-colors shadow-lg shadow-blue-600/20"
                    >
                      Send Password
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center py-6">
                    <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center text-3xl mx-auto mb-4 border border-emerald-500/20">📧</div>
                    <p className="text-emerald-400 font-bold text-lg mb-2">Password sent!</p>
                    <p className="text-slate-400 text-sm">
                      Check your email at <span className="text-white font-medium">{forgotEmail}</span> for your password.
                    </p>
                  </div>

                  <button
                    onClick={() => {
                      setShowForgotPassword(false);
                      setShowAdminLogin(true);
                      setForgotEmail('');
                      setForgotSuccess(false);
                    }}
                    className="w-full px-4 py-3 bg-white text-black hover:bg-slate-200 rounded-xl font-bold transition-colors mt-2"
                  >
                    Back to Login
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* User Login Modal */}
        {showUserLogin && (
          <div className="fixed inset-0 bg-[#020617]/80 backdrop-blur-md flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
            <div className="bg-[#0f172a] rounded-3xl border border-slate-700/30 p-8 max-w-sm w-full shadow-[0_20px_60px_rgba(0,0,0,0.5)] relative ring-1 ring-white/5">
              <h2 className="text-2xl font-bold text-white mb-6 font-display text-center">👋 Welcome Back</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-slate-400 text-sm font-medium mb-2">Email</label>
                  <input
                    type="email"
                    value={authData.email}
                    onChange={(e) => setAuthData({ ...authData, email: e.target.value })}
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all placeholder-slate-600"
                    placeholder="satoshi@example.com"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 text-sm font-medium mb-2">Password</label>
                  <input
                    type="password"
                    value={authData.password}
                    onChange={(e) => setAuthData({ ...authData, password: e.target.value })}
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all placeholder-slate-600"
                    placeholder="••••••••"
                  />
                </div>
                <button
                  onClick={handleUserLogin}
                  className="w-full py-3 bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-500 hover:to-orange-400 rounded-xl text-white font-bold transition-all shadow-lg shadow-orange-500/20 mt-2"
                >
                  Login
                </button>
                <p className="text-center text-slate-500 text-sm mt-4">
                  New here? <button onClick={() => { setShowUserLogin(false); setShowUserSignup(true); }} className="text-orange-400 hover:text-orange-300 font-medium">Sign Up</button>
                </p>
                <button onClick={() => setShowUserLogin(false)} className="w-full py-2 text-slate-500 hover:text-white text-sm transition-colors">Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* User Signup Modal */}
        {showUserSignup && (
          <div className="fixed inset-0 bg-[#020617]/80 backdrop-blur-md flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
            <div className="bg-[#0f172a] rounded-3xl border border-slate-700/30 p-8 max-w-sm w-full shadow-[0_20px_60px_rgba(0,0,0,0.5)] relative ring-1 ring-white/5">
              <h2 className="text-2xl font-bold text-white mb-6 font-display text-center">🚀 Create Account</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-slate-400 text-sm font-medium mb-2">Name / Nym</label>
                  <input
                    type="text"
                    value={authData.name}
                    onChange={(e) => setAuthData({ ...authData, name: e.target.value })}
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all placeholder-slate-600"
                    placeholder="Satoshi"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 text-sm font-medium mb-2">Email</label>
                  <input
                    type="email"
                    value={authData.email}
                    onChange={(e) => setAuthData({ ...authData, email: e.target.value })}
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all placeholder-slate-600"
                    placeholder="satoshi@example.com"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 text-sm font-medium mb-2">Password</label>
                  <input
                    type="password"
                    value={authData.password}
                    onChange={(e) => setAuthData({ ...authData, password: e.target.value })}
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all placeholder-slate-600"
                    placeholder="••••••••"
                  />
                </div>
                <button
                  onClick={handleUserSignup}
                  className="w-full py-3 bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-500 hover:to-orange-400 rounded-xl text-white font-bold transition-all shadow-lg shadow-orange-500/20 mt-2"
                >
                  Create Account
                </button>
                <p className="text-center text-slate-500 text-sm mt-4">
                  Already have an account? <button onClick={() => { setShowUserSignup(false); setShowUserLogin(true); }} className="text-orange-400 hover:text-orange-300 font-medium">Login</button>
                </p>
                <button onClick={() => setShowUserSignup(false)} className="w-full py-2 text-slate-500 hover:text-white text-sm transition-colors">Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Admin Panel Modal */}
        {showAdminPanel && currentAdmin && (
          <div className="fixed inset-0 bg-[#020617]/90 backdrop-blur-xl flex items-center justify-center p-4 z-50 animate-in fade-in duration-300">
            <div className="bg-[#0f172a] rounded-3xl border border-slate-700/30 p-6 md:p-8 max-w-4xl w-full h-[85vh] flex flex-col shadow-[0_20px_60px_rgba(0,0,0,0.5)] overflow-hidden relative ring-1 ring-white/5">

              {/* Admin Header - Compact */}
              <div className="flex items-center justify-between mb-8 shrink-0">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center text-2xl border border-slate-700/50">🔐</div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-white text-xl font-display">{currentAdmin.name}</span>
                      {currentAdmin.role === 'super' && (
                        <span className="bg-purple-500/20 text-purple-300 border border-purple-500/30 text-xs px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">Super Admin</span>
                      )}
                    </div>
                    <div className="text-slate-400 text-sm">{currentAdmin.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleAdminLogout}
                    className="text-slate-400 hover:text-white px-3 py-1.5 rounded-lg hover:bg-white/5 transition-colors text-sm font-medium"
                  >
                    Logout
                  </button>
                  <button
                    onClick={() => setShowAdminPanel(false)}
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-white transition-colors"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Your Meetups - Compact inline */}
              <div className="flex items-center gap-3 mb-6 flex-wrap shrink-0">
                <span className="text-slate-500 text-xs font-bold uppercase tracking-wider">Managing:</span>
                {getAdminMeetups().map(m => (
                  <span key={m.id} className="bg-slate-800/80 px-3 py-1 rounded-full text-slate-200 text-xs border border-slate-700/50 flex items-center gap-1">
                    <span>{m.country}</span>
                    <span className="font-medium">{m.name}</span>
                  </span>
                ))}
              </div>

              {/* Admin Tab Navigation */}
              <div className="flex gap-2 mb-6 bg-slate-900/50 p-1.5 rounded-2xl overflow-x-auto shrink-0 border border-slate-700/30 shadow-inner">
                {[
                  { id: 'stats', label: 'Dashboard', icon: '📊' },
                  { id: 'bounties', label: 'Bounties', icon: '📋' },
                  { id: 'merchants', label: 'Merchants', icon: '🏪' },
                  { id: 'meetups', label: 'Meetups', icon: '👥' },
                  { id: 'moderation', label: 'Moderation', icon: '💬' },
                  { id: 'settings', label: 'Settings', icon: '⚙️' },
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setAdminTab(tab.id)}
                    className={`px-4 py-2.5 rounded-xl text-sm font-bold transition-all whitespace-nowrap flex items-center gap-2 ${adminTab === tab.id
                      ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                      }`}
                  >
                    <span>{tab.icon}</span>
                    <span>{tab.label}</span>
                  </button>
                ))}
              </div>

              {/* Admin Content - Scrollable area */}
              <div className="overflow-y-auto flex-1 pr-2 -mr-2 custom-scrollbar">
                {/* Stats Tab */}
                {adminTab === 'stats' && (
                  <div className="space-y-6">
                    {/* Stats Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-slate-800/40 rounded-2xl p-6 border border-slate-700/50 backdrop-blur-sm text-center group hover:bg-slate-800/60 transition-colors">
                        <div className="text-4xl font-black text-orange-400 mb-1 group-hover:scale-110 transition-transform duration-300">
                          {apiBounties.filter(b => currentAdmin.meetups.includes(b.meetup) || currentAdmin.role === 'super').length}
                        </div>
                        <div className="text-slate-400 text-xs font-bold uppercase tracking-wider">Total Bounties</div>
                      </div>
                      <div className="bg-slate-800/40 rounded-2xl p-6 border border-slate-700/50 backdrop-blur-sm text-center group hover:bg-slate-800/60 transition-colors">
                        <div className="text-4xl font-black text-emerald-400 mb-1 group-hover:scale-110 transition-transform duration-300">
                          {completedBounties.filter(b => currentAdmin.meetups.includes(b.meetup) || currentAdmin.role === 'super').length}
                        </div>
                        <div className="text-slate-400 text-xs font-bold uppercase tracking-wider">Completed</div>
                      </div>
                      <div className="bg-slate-800/40 rounded-2xl p-6 border border-slate-700/50 backdrop-blur-sm text-center group hover:bg-slate-800/60 transition-colors">
                        <div className="text-4xl font-black text-blue-400 mb-1 group-hover:scale-110 transition-transform duration-300">{getAdminMeetups().length}</div>
                        <div className="text-slate-400 text-xs font-bold uppercase tracking-wider">Meetups</div>
                      </div>
                      <div className="bg-slate-800/40 rounded-2xl p-6 border border-slate-700/50 backdrop-blur-sm text-center group hover:bg-slate-800/60 transition-colors">
                        <div className="text-3xl font-black text-yellow-400 mb-1 group-hover:scale-110 transition-transform duration-300 truncate">
                          {formatSats(
                            apiBounties
                              .filter(b => currentAdmin.meetups.includes(b.meetup) || currentAdmin.role === 'super')
                              .reduce((acc, b) => acc + b.totalSats, 0)
                          )}
                        </div>
                        <div className="text-slate-400 text-xs font-bold uppercase tracking-wider">Total Pooled</div>
                      </div>
                    </div>

                    {/* Dashboard Content Grid */}
                    <div className="grid md:grid-cols-3 gap-6">
                      {/* Left Col: Growth Chart & Health */}
                      <div className="md:col-span-2 space-y-6">
                        {/* Growth Overview */}
                        <div className="bg-slate-800/40 rounded-2xl border border-slate-700/50 p-6 overflow-hidden relative">
                          <div className="flex items-center justify-between mb-6">
                            <h3 className="text-white font-bold text-lg">📈 Growth Overview</h3>
                            <select className="bg-slate-900/50 border border-slate-700 rounded-lg text-xs text-slate-300 px-2 py-1 focus:outline-none">
                              <option>Last 30 Days</option>
                              <option>Last Quarter</option>
                              <option>Year to Date</option>
                            </select>
                          </div>

                          {/* CSS Chart Mockup */}
                          <div className="h-48 flex items-end gap-2 px-2">
                            {[40, 65, 45, 80, 55, 90, 75, 85, 60, 95, 70, 100].map((h, i) => (
                              <div key={i} className="flex-1 flex flex-col justify-end group">
                                <div
                                  className={`w-full bg-gradient-to-t from-orange-600/20 to-orange-500/50 rounded-t-sm group-hover:from-orange-500/40 group-hover:to-orange-400/70 transition-all duration-300 relative`}
                                  style={{ height: `${h}%` }}
                                >
                                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-[10px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap border border-slate-700">
                                    {h * 1500} Sats
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="flex justify-between mt-2 text-xs text-slate-500 px-1">
                            <span>Jan 1</span>
                            <span>Jan 15</span>
                            <span>Jan 30</span>
                          </div>
                        </div>

                        {/* Recent Activity */}
                        <div className="bg-slate-800/40 rounded-2xl border border-slate-700/50 overflow-hidden">
                          <div className="px-6 py-4 border-b border-slate-700/50 flex justify-between items-center">
                            <h3 className="text-white font-bold text-lg">⚡ Recent Activity</h3>
                            <button className="text-xs text-orange-400 hover:text-orange-300">View All</button>
                          </div>
                          <div className="divide-y divide-slate-700/50">
                            {[
                              { icon: '💰', text: 'Bob pledged 50k sats to "Coffee Shop"', time: '2 mins ago', type: 'pledge' },
                              { icon: '🏪', text: 'New merchant "Barber King" claimed bounty', time: '1 hour ago', type: 'claim' },
                              { icon: '🆕', text: 'New bounty "Vegan Restaurant" created', time: '3 hours ago', type: 'new' },
                              { icon: '✅', text: 'Lisa verified "Beef Noodle Soup"', time: 'Yesterday', type: 'verify' },
                              { icon: '💬', text: 'New comment on "Bubble Tea Shop"', time: 'Yesterday', type: 'comment' },
                            ].map((item, i) => (
                              <div key={i} className="px-6 py-4 flex items-center gap-4 hover:bg-slate-800/50 transition-colors">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg ${item.type === 'pledge' ? 'bg-orange-500/10 text-orange-400' :
                                  item.type === 'claim' ? 'bg-purple-500/10 text-purple-400' :
                                    item.type === 'new' ? 'bg-blue-500/10 text-blue-400' :
                                      item.type === 'verify' ? 'bg-emerald-500/10 text-emerald-400' :
                                        'bg-slate-700/30 text-slate-400'
                                  }`}>
                                  {item.icon}
                                </div>
                                <div className="flex-1">
                                  <p className="text-slate-300 text-sm">{item.text}</p>
                                  <p className="text-slate-500 text-xs mt-0.5">{item.time}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Right Col: System Health & Notices */}
                      <div className="space-y-6">
                        {/* System Status */}
                        <div className="bg-slate-800/40 rounded-2xl border border-slate-700/50 p-6">
                          <h3 className="text-white font-bold text-lg mb-4">🖥️ System Status</h3>
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <span className="text-slate-400 text-sm">API Server</span>
                              <span className="flex items-center gap-1.5 text-xs font-bold text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded">
                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                                Operational
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-slate-400 text-sm">Lightning Node</span>
                              <span className="flex items-center gap-1.5 text-xs font-bold text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded">
                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                                Synced
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-slate-400 text-sm">Database</span>
                              <span className="flex items-center gap-1.5 text-xs font-bold text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded">
                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                                Connected
                              </span>
                            </div>
                            <div className="pt-4 border-t border-slate-700/50">
                              <div className="flex justify-between text-xs text-slate-500 mb-1">
                                <span>Server Load</span>
                                <span>12%</span>
                              </div>
                              <div className="w-full bg-slate-700/50 h-1.5 rounded-full overflow-hidden">
                                <div className="bg-blue-500 h-full w-[12%] rounded-full"></div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Admin Notices */}
                        <div className="bg-gradient-to-br from-blue-900/40 to-indigo-900/40 rounded-2xl border border-blue-500/20 p-6">
                          <h3 className="text-blue-200 font-bold text-lg mb-2">📢 Admin Notice</h3>
                          <p className="text-blue-300/80 text-sm leading-relaxed mb-4">
                            Monthly payout cycle processing is scheduled for this Friday at 12:00 UTC. Please review all pending verifications before then.
                          </p>
                          <button className="w-full py-2 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 rounded-lg text-blue-300 text-sm font-medium transition-colors">
                            Review Verifications
                          </button>
                        </div>

                        {/* Quick Tips */}
                        <div className="bg-slate-800/40 rounded-2xl border border-slate-700/50 p-6">
                          <h3 className="text-white font-bold text-lg mb-4">💡 Pro Tip</h3>
                          <p className="text-slate-400 text-sm italic">
                            "Encourage merchants to use their own Lightning node for better privacy and lower fees."
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                )}

                {/* Bounties Tab - Tile grid */}
                {adminTab === 'bounties' && (
                  <div className="bg-slate-800/40 rounded-2xl border border-slate-700/50 overflow-hidden">
                    <table className="w-full text-left text-sm text-slate-400">
                      <thead className="bg-slate-900/50 text-slate-200 font-bold uppercase text-xs">
                        <tr>
                          <th className="px-6 py-4">Status</th>
                          <th className="px-6 py-4">Bounty</th>
                          <th className="px-6 py-4">Sats</th>
                          <th className="px-6 py-4 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700/50">
                        {apiBounties.filter(b => currentAdmin.meetups.includes(b.meetup) || currentAdmin.role === 'super').map(b => (
                          <tr key={b.id} className="hover:bg-slate-700/20 transition-colors">
                            <td className="px-6 py-4">
                              <StatusBadge status={b.status} />
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-white font-medium">{b.businessType}</div>
                              <div className="text-xs truncate max-w-[200px]">{b.description}</div>
                              {b.businessName && <div className="text-xs text-amber-400 mt-1">{b.businessName}</div>}
                            </td>
                            <td className="px-6 py-4 font-mono text-orange-400">
                              {formatSats(b.totalSats)}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <button className="text-slate-400 hover:text-white px-2 transition-colors">✏️</button>
                              <button className="text-red-400 hover:text-red-300 px-2 transition-colors">🗑️</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {apiBounties.length === 0 && <div className="p-8 text-center text-slate-500">No bounties found.</div>}
                  </div>
                )}

                {/* Merchants Tab - Tile grid */}
                {adminTab === 'merchants' && (
                  <div className="bg-slate-800/40 rounded-2xl border border-slate-700/50 overflow-hidden">
                    <table className="w-full text-left text-sm text-slate-400">
                      <thead className="bg-slate-900/50 text-slate-200 font-bold uppercase text-xs">
                        <tr>
                          <th className="px-6 py-4">Merchant</th>
                          <th className="px-6 py-4">Loc</th>
                          <th className="px-6 py-4">Verify</th>
                          <th className="px-6 py-4 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700/50">
                        {/* Mock data for merchants since we don't have separate merchant list, reusing bounties with claims */}
                        {apiBounties.filter(b => b.status !== 'active' && (currentAdmin.meetups.includes(b.meetup) || currentAdmin.role === 'super')).map(b => (
                          <tr key={b.id + 'm'} className="hover:bg-slate-700/20 transition-colors">
                            <td className="px-6 py-4">
                              <div className="text-white font-medium">{b.businessName}</div>
                              <div className="text-xs text-slate-500">{b.businessType}</div>
                            </td>
                            <td className="px-6 py-4">
                              {b.googleMapsLink && <a href={b.googleMapsLink} target="_blank" className="text-blue-400 hover:underline text-xs">Map ↗</a>}
                            </td>
                            <td className="px-6 py-4">
                              <span className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2 py-1 rounded text-xs font-bold">
                                {b.verifications?.length || 0} Verifications
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <button className="text-slate-400 hover:text-white px-2">✏️</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {apiBounties.filter(b => b.status !== 'active').length === 0 && (
                      <div className="p-8 text-center text-slate-500">No merchants found.</div>
                    )}
                  </div>
                )}

                {adminTab === 'meetups' && (
                  <div className="bg-slate-800/40 rounded-2xl border border-slate-700/50 overflow-hidden">
                    <table className="w-full text-left text-sm text-slate-400">
                      <thead className="bg-slate-900/50 text-slate-200 font-bold uppercase text-xs">
                        <tr>
                          <th className="px-6 py-4">Meetup</th>
                          <th className="px-6 py-4">Members</th>
                          <th className="px-6 py-4">Bounties</th>
                          <th className="px-6 py-4">Status</th>
                          <th className="px-6 py-4 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700/50">
                        {getAdminMeetups().map(m => (
                          <tr key={m.id} className="hover:bg-slate-700/20 transition-colors">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <span className="text-2xl">{m.country}</span>
                                <div>
                                  <div className="text-white font-medium">{m.name}</div>
                                  <div className="text-xs text-slate-500">ID: {m.id}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className="bg-blue-500/10 text-blue-400 border border-blue-500/20 px-2 py-1 rounded text-xs font-bold">
                                {m.members} members
                              </span>
                            </td>
                            <td className="px-6 py-4 font-mono text-orange-400">
                              {apiBounties.filter(b => b.meetup === m.id).length} active
                            </td>
                            <td className="px-6 py-4">
                              <span className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2 py-1 rounded text-xs font-bold">
                                ✅ Active
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <button className="text-slate-400 hover:text-white px-2 transition-colors">✏️</button>
                              <button className="text-blue-400 hover:text-blue-300 px-2 transition-colors">👥</button>
                              {currentAdmin.role === 'super' && (
                                <button className="text-red-400 hover:text-red-300 px-2 transition-colors">🗑️</button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {getAdminMeetups().length === 0 && (
                      <div className="p-8 text-center text-slate-500">No meetups assigned to your account.</div>
                    )}
                  </div>
                )}

                {adminTab === 'moderation' && (
                  <div className="space-y-4">
                    {/* Moderation Stats */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-orange-400">0</div>
                        <div className="text-xs text-slate-400">Flagged</div>
                      </div>
                      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-blue-400">
                          {apiBounties.reduce((acc, b) => acc + (b.comments?.length || 0), 0)}
                        </div>
                        <div className="text-xs text-slate-400">Total Comments</div>
                      </div>
                      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-emerald-400">
                          {apiBounties.reduce((acc, b) => acc + (b.verifications?.length || 0), 0)}
                        </div>
                        <div className="text-xs text-slate-400">Verifications</div>
                      </div>
                    </div>

                    {/* Recent Comments Table */}
                    <div className="bg-slate-800/40 rounded-2xl border border-slate-700/50 overflow-hidden">
                      <div className="px-6 py-3 bg-slate-900/50 border-b border-slate-700/50">
                        <h3 className="text-white font-bold text-sm">💬 Recent Comments</h3>
                      </div>
                      <table className="w-full text-left text-sm text-slate-400">
                        <thead className="bg-slate-900/30 text-slate-300 font-medium text-xs">
                          <tr>
                            <th className="px-6 py-3">Bounty</th>
                            <th className="px-6 py-3">Comment</th>
                            <th className="px-6 py-3">Author</th>
                            <th className="px-6 py-3 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/50">
                          {apiBounties.flatMap(b =>
                            (b.comments || []).map((c, i) => ({
                              bountyType: b.businessType,
                              bountyId: b.id,
                              comment: c,
                              key: `${b.id}-${i}`
                            }))
                          ).slice(0, 10).map(item => (
                            <tr key={item.key} className="hover:bg-slate-700/20 transition-colors">
                              <td className="px-6 py-3">
                                <span className="text-white">{item.bountyType}</span>
                              </td>
                              <td className="px-6 py-3">
                                <div className="max-w-[300px] truncate">{item.comment.text}</div>
                              </td>
                              <td className="px-6 py-3 text-slate-300">{item.comment.name}</td>
                              <td className="px-6 py-3 text-right">
                                <button className="text-orange-400 hover:text-orange-300 px-2 transition-colors">🚩</button>
                                <button className="text-red-400 hover:text-red-300 px-2 transition-colors">🗑️</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {apiBounties.flatMap(b => b.comments || []).length === 0 && (
                        <div className="p-8 text-center text-slate-500">No comments to moderate.</div>
                      )}
                    </div>

                    {/* Quick Actions */}
                    <div className="grid grid-cols-2 gap-4">
                      <button className="flex items-center justify-center gap-3 p-4 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20 rounded-xl text-blue-300 font-medium transition-colors">
                        <span>📧</span> Send Email Blast
                      </button>
                      <button className="flex items-center justify-center gap-3 p-4 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 rounded-xl text-emerald-300 font-medium transition-colors">
                        <span>📥</span> Export CSV
                      </button>
                    </div>
                  </div>
                )}

                {/* Settings Tab */}
                {adminTab === 'settings' && (
                  <div className="space-y-4 max-w-xl mx-auto">
                    {/* Account Info - Compact */}
                    <div className="bg-slate-800/40 rounded-2xl p-6 border border-slate-800">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="p-3 bg-black/20 rounded-xl">
                          <span className="text-slate-500 text-xs font-bold uppercase tracking-wider block mb-1">Email</span>
                          <div className="text-white truncate font-mono">{currentAdmin.email}</div>
                        </div>
                        <div className="p-3 bg-black/20 rounded-xl">
                          <span className="text-slate-500 text-xs font-bold uppercase tracking-wider block mb-1">Role</span>
                          <div className="text-white capitalize font-medium">{currentAdmin.role}</div>
                        </div>
                      </div>
                    </div>

                    {/* Change Password */}
                    {!showChangePassword ? (
                      <button
                        onClick={() => setShowChangePassword(true)}
                        className="w-full flex items-center justify-center gap-4 p-6 bg-slate-800/40 hover:bg-slate-800/60 rounded-2xl transition-all border border-slate-800 group"
                      >
                        <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center text-xl text-blue-400 group-hover:scale-110 transition-transform">🔑</div>
                        <div className="text-left">
                          <div className="font-bold text-white text-lg">Change Password</div>
                          <div className="text-sm text-slate-400">Update your admin password</div>
                        </div>
                      </button>
                    ) : (
                      <div className="bg-slate-800/40 rounded-2xl p-6 border border-slate-800">
                        {passwordChangeSuccess ? (
                          <div className="text-center py-4">
                            <div className="w-12 h-12 bg-emerald-500/10 rounded-full flex items-center justify-center text-2xl mx-auto mb-2 text-emerald-400">✅</div>
                            <p className="text-emerald-400 font-bold">Password updated successfully!</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <h3 className="font-bold text-white mb-2">New Password</h3>
                            <input
                              type="password"
                              value={newPassword}
                              onChange={(e) => {
                                setNewPassword(e.target.value);
                                setPasswordChangeError('');
                              }}
                              className="w-full bg-black/20 border border-slate-700/50 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-500/50"
                              placeholder="New password (min 6 chars)"
                            />
                            <input
                              type="password"
                              value={confirmPassword}
                              onChange={(e) => {
                                setConfirmPassword(e.target.value);
                                setPasswordChangeError('');
                              }}
                              className="w-full bg-black/20 border border-slate-700/50 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-500/50"
                              placeholder="Confirm password"
                            />

                            {passwordChangeError && (
                              <p className="text-red-400 text-xs bg-red-900/20 p-2 rounded-lg">{passwordChangeError}</p>
                            )}

                            <div className="flex gap-3 pt-2">
                              <button
                                onClick={() => {
                                  setShowChangePassword(false);
                                  setNewPassword('');
                                  setConfirmPassword('');
                                  setPasswordChangeError('');
                                }}
                                className="flex-1 px-4 py-2 bg-slate-700/50 hover:bg-slate-700 rounded-xl text-white text-sm font-medium transition-colors"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleChangePassword}
                                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-xl text-white text-sm font-bold transition-colors shadow-lg shadow-blue-600/20"
                              >
                                Update Password
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Add New Meetup Modal */}
        {showNewMeetup && (
          <div className="fixed inset-0 bg-[#020617]/80 backdrop-blur-md flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
            <div className="bg-[#0f172a] rounded-3xl border border-slate-700/30 p-8 max-w-md w-full shadow-[0_20px_60px_rgba(0,0,0,0.5)] relative ring-1 ring-white/5">
              <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-orange-600 via-orange-400 to-orange-600"></div>
              <h2 className="text-2xl font-bold text-white mb-2 font-display">Add Your Meetup</h2>
              <p className="text-slate-400 mb-6">Bring the bounty board to your local Bitcoin community</p>

              <div className="space-y-4">
                <div>
                  <label className="block text-slate-400 text-sm font-medium mb-2">Meetup Name</label>
                  <input
                    type="text"
                    value={newMeetupData.name}
                    onChange={(e) => setNewMeetupData({ ...newMeetupData, name: e.target.value })}
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all placeholder-slate-600"
                    placeholder="e.g., Singapore Bitcoin"
                  />
                </div>

                <div>
                  <label className="block text-slate-400 text-sm font-medium mb-2">Country Flag + Name</label>
                  <input
                    type="text"
                    value={newMeetupData.country}
                    onChange={(e) => setNewMeetupData({ ...newMeetupData, country: e.target.value })}
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all placeholder-slate-600"
                    placeholder="e.g., 🇸🇬 Singapore"
                  />
                </div>

                <div>
                  <label className="block text-slate-400 text-sm font-medium mb-2">Your Email</label>
                  <input
                    type="email"
                    value={newMeetupData.email}
                    onChange={(e) => setNewMeetupData({ ...newMeetupData, email: e.target.value })}
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all placeholder-slate-600"
                    placeholder="organizer@meetup.com"
                  />
                  <p className="text-slate-500 text-xs mt-1">You'll receive admin credentials once approved</p>
                </div>
              </div>

              <div className="flex gap-3 mt-8">
                <button
                  onClick={() => setShowNewMeetup(false)}
                  className="flex-1 px-4 py-3 bg-slate-800 hover:bg-slate-700 rounded-xl text-white font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRequestMeetup}
                  className="flex-1 px-4 py-3 bg-orange-600 hover:bg-orange-500 rounded-xl text-white font-bold transition-colors shadow-lg shadow-orange-600/20"
                >
                  Submit Meetup
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Contribute Modal */}
        {showContribute && (
          <div className="fixed inset-0 bg-[#020617]/80 backdrop-blur-md flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
            <div className="bg-[#0f172a] rounded-3xl border border-slate-700/30 p-8 max-w-md w-full shadow-[0_20px_60px_rgba(0,0,0,0.5)] relative ring-1 ring-white/5">
              <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-orange-400 via-yellow-400 to-orange-400"></div>
              <h2 className="text-2xl font-bold text-white mb-6 font-display">Add to This Bounty</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-slate-400 text-sm font-medium mb-2">Your Pledge (sats)</label>
                  <input
                    type="number"
                    value={contributeData.pledge}
                    onChange={(e) => setContributeData({ ...contributeData, pledge: e.target.value })}
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all placeholder-slate-600"
                    placeholder="50000"
                  />
                </div>

                <div>
                  <label className="block text-slate-400 text-sm font-medium mb-2">Your Name / Nym</label>
                  <input
                    type="text"
                    value={contributeData.name}
                    onChange={(e) => setContributeData({ ...contributeData, name: e.target.value })}
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all placeholder-slate-600"
                    placeholder="Anonymous"
                  />
                </div>

                <div>
                  <label className="block text-slate-400 text-sm font-medium mb-2">Email (for notifications)</label>
                  <input
                    type="email"
                    value={contributeData.email}
                    onChange={(e) => setContributeData({ ...contributeData, email: e.target.value })}
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all placeholder-slate-600"
                    placeholder="stacker@example.com"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-8">
                <button
                  onClick={() => setShowContribute(null)}
                  className="flex-1 px-4 py-3 bg-slate-800 hover:bg-slate-700 rounded-xl text-white font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleContribute}
                  className="flex-1 px-4 py-3 bg-orange-600 hover:bg-orange-500 rounded-xl text-white font-bold transition-colors shadow-lg shadow-orange-600/20"
                >
                  Add Sats
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="mt-16 bg-[#0f172a]/50 backdrop-blur-sm rounded-2xl border border-white/5 p-8 text-center md:text-left relative overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-1 bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-6 relative z-10">
            <div>
              <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-orange-600 flex items-center justify-center text-white font-bold">₿</div>
                <span className="font-display font-bold text-white text-xl tracking-tight">Bounty Board</span>
              </div>
              <p className="text-slate-400 text-sm">Built by <span className="text-orange-400 hover:text-orange-300 transition-colors cursor-pointer">Murph & Stephen</span> for bitcoin++ Taipei 2025</p>
            </div>

            <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8">
              <a
                href="https://btcplusplus.dev/conf/taipei"
                className="flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm font-medium bg-white/5 hover:bg-white/10 px-4 py-2 rounded-full border border-white/5"
              >
                <span>✨</span>
                <span>btcplusplus.dev</span>
              </a>
              <div className="text-slate-500 text-sm">
                Trust, Verify, Stack.
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default BountyBoard;
